import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { StepDirection } from '@apps-common/apps/portal/pages/orders/pages/create/components/management'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputInfoSM, InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { CreateOrderSteps } from '@apps-common/config/orders'
import { useManagementOrderSelectItems } from '@apps-common/hooks/useManagementOrderSelectItems'
import { toastWarning } from '@apps-common/lib/toast'
import { IProductListModel } from '@apps-common/types/management/product'
import { formatPrice } from '@apps-common/utils/helpers'

interface IProps {
  isLoading: boolean
  isUpdating: boolean
  step: CreateOrderSteps
  move: (direction: StepDirection) => void
}

export const ManagementOrderlineForm: React.FC<IProps> = ({ isLoading, isUpdating, step, move }) => {
  const { control, setValue, watch, formState } = useFormContext()
  const { errors } = formState
  const { isLoading: selectItemsIsLoading, products } = useManagementOrderSelectItems()

  const [selectedProduct, setSelectedProduct] = useState<IProductListModel | null>(null)
  const [selectedProductQuantity, setSelectedProductQuantity] = useState<number>(1)
  const [selectedProductDiscount, setSelectedProductDiscount] = useState<number>(0)
  const [selectedProductIsInactiveUnit, setSelectedProductIsInactiveUnit] = useState<boolean>(false)

  const {
    fields: orderLineItems,
    append,
    update,
    remove
  } = useFieldArray({
    control,
    name: 'orderLineItems',
    rules: {
      validate: (value) => {
        if (step !== CreateOrderSteps.details && value?.length === 0) {
          return 'Order requires at least one item added.'
        }

        return true
      }
    }
  })

  const resetOrderline = () => {
    setValue('orderLineItems', [])
    setSelectedProduct(null)
    setSelectedProductQuantity(1)
    setSelectedProductDiscount(0)
    setSelectedProductIsInactiveUnit(false)
  }

  const add = () => {
    const product = products?.find((p) => p.productId === selectedProduct?.productId)

    if (!product || selectedProductQuantity === 0) {
      toastWarning('Product and quantity required.')

      return
    }

    let discountAmount = 0

    if (selectedProductDiscount) {
      discountAmount = (selectedProduct?.price || 0) * selectedProductQuantity * (selectedProductDiscount / 100)
    }

    append({
      productId: product.productId,
      productName: product.productName,
      productType: product.productType,
      productQuantity: selectedProductQuantity,
      discount: selectedProductDiscount,
      cost: product.price * selectedProductQuantity - discountAmount,
      isInactiveUnit: selectedProductIsInactiveUnit,
      requiresAction: product.actionRequired,
      orderLineReady: !product.actionRequired,
      orderLineExtraActionItems: Array.from(Array(selectedProductQuantity).keys()).map(() => null)
    })

    setSelectedProduct(null)
    setSelectedProductQuantity(1)
    setSelectedProductDiscount(0)
    setSelectedProductIsInactiveUnit(false)
  }

  const removeLineItem = (index: number) => {
    remove(index)
  }

  const getTotal = () => {
    if (orderLineItems?.length === 0) return 0

    return orderLineItems.reduce((acc, curr: any) => acc + curr.cost, 0)
  }

  const getProductSubtotal = () => {
    if (!selectedProduct) return '$0.00'

    const currency = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })

    const lineCost = selectedProduct.price * selectedProductQuantity
    let discount = 0

    if (selectedProductDiscount >= 0 && selectedProductDiscount <= 100) {
      discount = lineCost * (selectedProductDiscount / 100)
    }

    return currency.format(lineCost - discount)
  }

  useEffect(() => {
    const total = getTotal()

    setValue('cost', total)
  }, [orderLineItems])

  const cost = watch('cost')

  const loading = isLoading || selectItemsIsLoading
  const updating = isUpdating

  return (
    <CollapsibleContent
      key='order-create-orderline-form'
      title='Step 2 | Orderline'
      defaultOpen={step === CreateOrderSteps.orderline}
      disabled={step !== CreateOrderSteps.orderline}
      classes='bg-surface border border-border rounded-2xl'
    >
      <div className='grid sm:grid-cols-2 gap-x-[10px]'>
        <SelectInputSM
          key='productId'
          type='text'
          placeholder='Select product'
          label='Product'
          id='productId'
          showLabel
          containerClassName='grow'
          loading={loading}
          disabled={updating}
          registration={{
            value: selectedProduct?.productId || '',
            onChange: (e: any) => {
              const product = products?.find((p) => p.productId === e?.target?.value)

              if (product) {
                setSelectedProduct(product)
              }
            }
          }}
          options={[...(products?.map((p) => ({ label: p.productName, value: p.productId })) || [])]}
          errors={errors?.contactId as any}
        />
        <InputSM
          type='number'
          placeholder='Product quantity'
          label='Quantity'
          id='productQuantity'
          showLabel
          containerClassName='grow sm:grow-none sm:min-w-[220px]'
          loading={loading}
          disabled={updating}
          registration={{
            value: selectedProductQuantity,
            onChange: (e: any) => setSelectedProductQuantity(parseInt(e?.target?.value, 10))
          }}
          errors={errors.orderNo as any}
        />
        <InputSM
          type='number'
          placeholder='Discount Percentage'
          label='Discount %'
          id='productDiscount'
          showLabel
          containerClassName='grow'
          loading={loading}
          disabled={updating}
          registration={{
            value: selectedProductDiscount,
            onChange: (e: any) => setSelectedProductDiscount(parseInt(e?.target?.value, 10)),
            min: 0,
            max: 100
          }}
          errors={errors.orderNo as any}
        />
        <InputInfoSM
          id='selectedProductTotal'
          showLabel
          label='Subtotal'
          loading={loading}
          value={getProductSubtotal()}
          containerClassName='grow'
          className='!bg-secondary-content/10'
        />
        {!selectedProduct?.actionRequired && <div />}
        <div className='sm:col-span-2 flex items-start justify-end'>
          <div>
            <ButtonRoundedSm
              type='button'
              className='inline-block action-brand-solid w-auto !h-[26px] !px-[10px] !pl-3 !min-w-0 !gap-[5px]'
              loading={false}
              text='Add'
              icon={<commonIcons.add />}
              iconRight
              callback={() => add()}
            />
          </div>
        </div>
      </div>
      <VertSpacerLg />
      <div className='overflow-hidden border border-border rounded-xl mt-[1rem] text-text'>
        <div className='jost-medium text-[0.9em] bg-surface-2 py-[0.5rem] sm:py-[1rem] px-[1rem] rounded-t-xl'>
          <div className='flex items-center justify-start gap-[10px]'>
            <div>Order items</div>
          </div>
        </div>
        {/* <div className='' /> */}
        {orderLineItems?.length === 0 && (
          <div>
            <p className='col-span-5 jost-light text-[0.8em] sm:text-[0.9em] p-4'>No products added</p>
          </div>
        )}
        {orderLineItems?.length > 0 &&
          orderLineItems.map((field: any, index) => (
            <div key={`orderline-item-${index}`} className='jost-light text-[0.8em] sm:text-[0.9em] border-b border-border p-[10px]'>
              <div className='flex items-center justify-between gap-[5px]'>
                <p className='grow flex items-center justify-start gap-[5px]'>
                  {field.productQuantity} x {field.productName}
                  <button
                    type='button'
                    aria-label='Remove line item'
                    className='action-error text-[0.8rem] leading-[0.9rem] jost-medium border-2 px-2'
                    onClick={() => removeLineItem(index)}
                  >
                    remove
                    {/* <commonIcons.dash className='text-[0.8rem]' /> */}
                  </button>
                </p>

                {field.discount > 0 && <p className='text-[0.75em] jost-medium'>{field.discount}% discount</p>}

                <p>{formatPrice(field.cost, 2)}</p>
              </div>
            </div>
          ))}
        <div className='jost-medium text-[0.9em] bg-surface-2 py-[0.5rem] sm:py-[1rem] px-[1rem] rounded-b-xl'>
          <div className='flex items-center justify-end gap-[10px]'>
            <div>Total: {formatPrice(cost, 2)}</div>
          </div>
        </div>
      </div>
      <p className='pl-2 pt-2 text-red-600 text-xs'>{errors?.orderLineItems?.root?.message as any}</p>

      <VertSpacerMd />
      <div className='flex items-center justify-end gap-[10px]'>
        <ModalButton
          classes='grow sm:grow-0'
          component={ConfirmModal}
          componentProps={{
            text: 'Go back',
            description: 'Moving back will clear the order line items, would you like to proceed?',
            action: ConfirmModalAction.Warning
          }}
          disabled={updating}
          confirmCallback={() => {
            move('backward')
            resetOrderline()
          }}
        >
          <ButtonRoundedSm
            type='div'
            loading={updating}
            text='Previous'
            icon={<commonIcons.arrowAngledLeft />}
            className='action-neutral'
          />
        </ModalButton>
        <ButtonRoundedSm
          type='submit'
          loading={updating}
          text='Next'
          icon={<commonIcons.arrowRight />}
          iconRight
          className='grow sm:grow-0 action-success'
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
